import type {
  IOrganizationData,
  OrganizationStatus,
  SharingPolicy,
  TourSharingPolicy,
} from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:organizationApi');

export interface GetOrganizationArgs {
  id: string;
}

export interface UpdateOrganizationArgs {
  id: string;
  icon?: string;
  logo?: string;
  organizationName?: string;
  sharingPolicy?: SharingPolicy;
  tourSharingPolicy?: TourSharingPolicy;
}

export interface DeleteOrganizationArgs {
  id: string;
}

export interface CreateOrganizationArgs {
  icon: string;
  logo: string;
  organizationName: string;
  organizationType: OrganizationStatus;
  hubspotCompany?: {
    id: string;
    name?: string;
  };
}

type GetUserStatsArgs = { organizationId: string };

type GetUserStatsResponse = {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  pendingInvites: number;
  usersWithExternalEmailDomains?: number;
  activeUsersByIp: number;
};
const organizationApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganization: builder.query<IOrganizationData, GetOrganizationArgs>({
      query: ({ id }) => ({
        url: `/api/organization/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'Organization', id }],
    }),

    updateOrganization: builder.mutation<
      IOrganizationData,
      UpdateOrganizationArgs
    >({
      query: ({
        id,
        icon,
        logo,
        organizationName,
        sharingPolicy = {},
        tourSharingPolicy,
      }) => ({
        url: `/api/organization/${id}`,
        method: 'POST',
        body: {
          icon,
          logo,
          organizationName,
          ...sharingPolicy,
          tourSharingPolicy,
        },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'Organizations' },
        { type: 'AdminOrganizations' },
        { type: 'Organization', id },
      ],
    }),

    deleteOrganization: builder.mutation<
      IOrganizationData,
      DeleteOrganizationArgs
    >({
      query: ({ id }) => ({
        url: `/api/organization/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'Organizations' },
        { type: 'AdminOrganizations' },
        { type: 'Organization', id },
      ],
    }),

    createOrganization: builder.mutation<
      IOrganizationData,
      CreateOrganizationArgs
    >({
      query: ({
        icon,
        logo,
        organizationName,
        organizationType,
        hubspotCompany,
      }) => ({
        url: '/api/organizations/new',
        method: 'PUT',
        body: {
          icon,
          logo,
          organizationName,
          state: organizationType,
          hubspotCompany,
        },
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Organizations' },
              { type: 'AdminOrganizations' },
              { type: 'Organization', id: result.id },
              { type: 'User' },
            ]
          : [{ type: 'Organizations' }, { type: 'AdminOrganizations' }],
    }),

    getOrganizationsByUser: builder.query<IOrganizationData[], void>({
      query: () => ({
        url: '/api/organizations',
        method: 'GET',
      }),
      providesTags: ['Organizations'],
    }),
    getUserStats: builder.query<GetUserStatsResponse, GetUserStatsArgs>({
      query: ({ organizationId }) => ({
        method: 'GET',
        url: `/api/organization/${organizationId}/user-stats`,
      }),
    }),
  }),
});

export const {
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  useGetOrganizationsByUserQuery,
  useUpdateOrganizationMutation,
  useCreateOrganizationMutation,
  useGetUserStatsQuery,
} = organizationApi;
